import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { waLink, API, renderLinkedText } from "@/lib/content";

const inputCls =
  "w-full rounded-xl border border-[#FAF8F5]/20 bg-[#FAF8F5]/5 px-5 py-4 text-sm text-[#FAF8F5] placeholder:text-[#FAF8F5]/40 transition-[border-color,background-color] duration-300 focus:border-[#C5A059] focus:bg-[#FAF8F5]/10";

export const EnquiryForm = () => {
  const { content } = useContent();
  const eq = content.enquiry;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    parents_city: "",
    property_location: "",
    service_required: "",
    package_interest: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSent(true);
      toast.success("Thank you! Your enquiry is with our Tricity team.");
    } catch {
      toast.error("Something went wrong — please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" className="bg-[#7A2B21] py-28 lg:py-40" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C5A059]">
            {eq.kicker}
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl">
            {eq.title_line1}{" "}
            <span className="italic text-[#C5A059]">{eq.title_line2}</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#FAF8F5]/80">
            {renderLinkedText(eq.intro)}
          </p>
          <div className="mt-10 space-y-4">
            <a
              href={waLink(content.business.whatsapp_number, eq.whatsapp_msg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-button"
              className="inline-flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#1C1917] transition-[background-color,transform] duration-300 hover:bg-[#1fb857] hover:scale-[1.03] active:scale-[0.98]"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
            </a>
            <p className="text-sm text-[#FAF8F5]/70" data-testid="enquiry-phone-display">
              Or call us directly:{" "}
              <a href={`tel:${content.business.phone_display.replace(/\s/g, "")}`} className="font-bold text-[#C5A059] underline-offset-4 hover:underline">
                {content.business.phone_display}
              </a>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          {sent ? (
            <div
              className="flex h-full flex-col items-start justify-center rounded-3xl border border-[#FAF8F5]/20 bg-[#FAF8F5]/5 p-12 backdrop-blur-sm"
              data-testid="enquiry-success-message"
            >
              <h3 className="font-display text-3xl font-medium text-[#FAF8F5]">
                Thank you, {form.name.split(" ")[0]}.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#FAF8F5]/80">
                Your enquiry has reached our Tricity team. We'll call you within
                24 hours. Want to talk sooner?
              </p>
              <a
                href={waLink(
                  content.business.whatsapp_number,
                  `Hi! I just submitted an enquiry on your website. My name is ${form.name}.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquiry-success-whatsapp"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-xs font-bold uppercase tracking-wider text-[#1C1917] transition-transform duration-300 hover:scale-[1.03]"
              >
                <MessageCircle size={15} />
                Continue on WhatsApp
              </a>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-3xl border border-[#FAF8F5]/20 bg-[#FAF8F5]/5 p-8 backdrop-blur-sm lg:p-10"
              data-testid="enquiry-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                  data-testid="enquiry-name-input"
                />
                <input
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone / WhatsApp number"
                  className={inputCls}
                  data-testid="enquiry-phone-input"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Your email"
                  className={inputCls}
                  data-testid="enquiry-email-input"
                />
                <input
                  value={form.country}
                  onChange={set("country")}
                  placeholder="Country of residence (e.g. Canada)"
                  className={inputCls}
                  data-testid="enquiry-country-input"
                />
                <input
                  value={form.parents_city}
                  onChange={set("parents_city")}
                  placeholder="Where do your parents live? (e.g. Mohali)"
                  className={inputCls}
                  data-testid="enquiry-city-input"
                />
                <input
                  value={form.property_location}
                  onChange={set("property_location")}
                  placeholder="Property location (if any)"
                  className={inputCls}
                  data-testid="enquiry-property-input"
                />
                <select
                  value={form.service_required}
                  onChange={set("service_required")}
                  className={`${inputCls} sm:col-span-2 [&>option]:text-[#1C1917]`}
                  data-testid="enquiry-service-select"
                >
                  <option value="">Service required *</option>
                  <option value="NRI Parent Care">NRI Parent Care</option>
                  <option value="NRI Property Management">NRI Property Management</option>
                  <option value="Both">Both — Parent Care + Property</option>
                  <option value="Other">Other / Not sure yet</option>
                </select>
                <select
                  value={form.package_interest}
                  onChange={set("package_interest")}
                  className={`${inputCls} sm:col-span-2 [&>option]:text-[#1C1917]`}
                  data-testid="enquiry-package-select"
                >
                  <option value="">Which package interests you? (optional)</option>
                  {content.packages.plans.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name} — {p.price}{p.period}
                    </option>
                  ))}
                  <option value="Not sure yet">Not sure yet — help me choose</option>
                </select>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your parents or property — routine, appointments, anything that matters."
                  className={`${inputCls} resize-none sm:col-span-2`}
                  data-testid="enquiry-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FAF8F5] py-4 text-sm font-bold uppercase tracking-wider text-[#7A2B21] transition-[background-color,transform] duration-300 hover:bg-[#C5A059] hover:text-[#1C1917] hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {sending ? "Sending..." : "Send My Enquiry"}
              </button>
              <p className="mt-4 text-center text-[11px] text-[#FAF8F5]/50">
                No spam, no pressure. Just a warm call back from our family to yours.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
